import { usePageModels } from '@/context/PageModels';
import { AssetDownloadWrapper, Chip, Image, PartnerOnly, RichText } from '@/features/shared';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import useDownloadResultContent from '@/features/shared/Hooks/useDownloadResultContent';
import { MuiButtonBase, MuiCard, MuiHidden, MuiTypography } from '@/features/shared/material-ui';
import {
  getChipCategoryLabel,
  getPageUrl,
  mapColorNameToContrastColorName,
  mapLinkToProps,
  matchExternalLink,
} from '@/utils/helpers';
import { BrPageContext } from '@bloomreach/react-sdk';
import clsx from 'clsx';
import React, { memo, PropsWithChildren, useCallback, useContext, useMemo } from 'react';
import { CardResultProps } from './Card.interface';
import useStyles from './Card.styles';

const CardResult = ({
  tags,
  image,
  title,
  description,
  theme,
  date,
  url,
  category,
  categoryType,
  contentCategory = '',
  cardColor,
  categoryUrl,
  resultID,
  noBleeding,
  isPartnerOnly,
  download,
}: PropsWithChildren<CardResultProps>) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const page = useContext(BrPageContext);
  const {
    contentCategoryCardDateLabelMapper,
    taxonomy: taxonomies = {},
    esLabels,
  } = usePageModels();
  const cardContrastColor = useMemo(
    () => mapColorNameToContrastColorName({ colorName: cardColor }),
    [cardColor],
  );
  const classes = useStyles({});
  const HAS_IMAGE = !!image;
  const MAX_LENGTH_TITLE = 150;
  const hasCategory = (category && category.length > 0) || contentCategory;
  const HREF = useMemo(() => page && getPageUrl({ url: url || '', page }), [page, url]);
  const linkProps = useMemo(
    () => mapLinkToProps({ href: HREF, openInNewWindow: Boolean(HREF && matchExternalLink(HREF)) }),
    [HREF],
  );

  const {
    queryParams,
    updateQueryParams,
    forceUpdate = () => null,
    labels = {},
  } = useContext(ElasticComponentContext);

  const { downloadContent, isDownloading, parsedErrorMessage, error } = useDownloadResultContent(
    url || '',
    contentRef,
  );

  const handleKeywordClick = (e: MouseEvent, keyword: string) => {
    e.preventDefault();

    if (Array.isArray(queryParams.keywords)) {
      updateQueryParams('keywords', [...queryParams.keywords, keyword]);
    } else if (queryParams.keywords?.length > 0) {
      updateQueryParams('keywords', [queryParams.keywords, keyword]);
    } else {
      updateQueryParams('keywords', [keyword]);
    }
  };

  const handleCategoryClick = useCallback(
    ({ e, value, clickType }: { e: React.MouseEvent; value: string; clickType: string }) => {
      e.preventDefault();
      if (!clickType) {
        return;
      }

      const currentValue = queryParams.filters[clickType] || [];
      if (currentValue.includes(value)) {
        return;
      }

      let parsedValue: string[] = [value];

      if (Array.isArray(currentValue)) {
        parsedValue = [...currentValue, value];
      }
      if (currentValue?.length > 0) {
        parsedValue = [...currentValue, value];
      }

      updateQueryParams(clickType, parsedValue, true);
      forceUpdate();

      e.stopPropagation();
    },
    [updateQueryParams, queryParams, forceUpdate],
  );

  const categories = useMemo(() => {
    const categoryProps = (arg: string) => {
      if (categoryUrl && categoryUrl[arg]) {
        const href = page && getPageUrl({ url: categoryUrl[arg] || '', page });
        return { ...mapLinkToProps({ href }), 'aria-label': arg };
      }

      return { component: MuiButtonBase };
    };

    const isCategory = !!(category && category?.length);
    let categoryWrapped = (isCategory ? category : [contentCategory]) || [];

    if (!Array.isArray(categoryWrapped)) {
      categoryWrapped = [categoryWrapped];
    }
    const clickType = (isCategory ? categoryType : 'tax_content_category') || '';
    const chipLabel = getChipCategoryLabel({ isCategory, labels: esLabels });

    return categoryWrapped.map((item, index) => (
      <Chip
        className={classes.chip}
        clickable
        label={chipLabel(item)}
        key={`${item}_${index}`}
        size="small"
        theme={theme}
        isCategory
        hoverable={false}
        multiple
        chipColor={cardContrastColor}
        onClick={
          categoryUrl && !categoryUrl[item || contentCategory]
            ? (e: React.MouseEvent) => handleCategoryClick({ e, value: item, clickType })
            : undefined
        }
        {...categoryProps(item || contentCategory)}
      />
    ));
  }, [
    category,
    contentCategory,
    categoryType,
    categoryUrl,
    page,
    classes.chip,
    theme,
    cardContrastColor,
    handleCategoryClick,
    esLabels,
  ]);

  const rootClassName = clsx(
    classes.root,
    `${theme}-theme`,
    `bg-color-${cardColor}`,
    noBleeding && 'no-bleeding',
  );
  const contentCategoryKey = React.useMemo(() => {
    const taxonomyIndex = Object.values(taxonomies).findIndex(
      (taxonomy: string) => taxonomy === contentCategory,
    );
    // We shouldn't expect duplicated values with different keys.
    return Object.keys(taxonomies)[taxonomyIndex] || '';
  }, [contentCategory, taxonomies]);
  const dateLabelField = contentCategoryCardDateLabelMapper?.[contentCategoryKey] || 'published';

  return (
    <div className={classes.button} ref={contentRef}>
      <MuiCard className={rootClassName} elevation={3} data-testid="qa-card-component">
        <AssetDownloadWrapper
          download={download}
          isDownloading={isDownloading}
          error={error}
          errorMessage={parsedErrorMessage}
        >
          <article className={classes.cardContent}>
            {HAS_IMAGE && (
              <div className={classes.image} data-testid="qa-card-component-image">
                <Image
                  image={{ original: { url: image || '' }, altText: title }}
                  base="original"
                  size="cover"
                  className={classes.img}
                />
              </div>
            )}
            <div className={classes.content}>
              <header>
                {(hasCategory || date) && (
                  <div className={classes.header}>
                    <div className="search-header">
                      {hasCategory && categories}
                      {date && (
                        <MuiHidden implementation="css" smUp>
                          {hasCategory && <div className={classes.spacer} />}
                          <MuiTypography variant="caption" component="span">
                            {labels?.[dateLabelField] && `${labels?.[dateLabelField]}: `}
                            {date}
                          </MuiTypography>
                        </MuiHidden>
                      )}
                    </div>
                    {isPartnerOnly && (
                      <PartnerOnly className="header-partner-only" blueLabel theme={theme} />
                    )}
                  </div>
                )}
                {title && (
                  <MuiTypography variant="h5" className={classes.title}>
                    {window.innerWidth < 480 && title.length > MAX_LENGTH_TITLE
                      ? `${title.substring(0, MAX_LENGTH_TITLE - 3)}...`
                      : title}
                  </MuiTypography>
                )}
              </header>
              <main className={classes.main}>
                {description && <RichText className={classes.description} content={description} />}
              </main>
              {(tags?.length || date) && (
                <>
                  <div className={classes.spacer} />
                  <footer className={classes.footer}>
                    {tags && (
                      <div className={classes.tagsWrapper}>
                        {tags?.length > 0 && (
                          <>
                            <MuiTypography variant="overline" component="span" className="title">
                              {`${labels?.tags || 'TAGS'}`}:
                            </MuiTypography>
                            <div className={classes.tags}>
                              {tags.map((item: any, id) => (
                                <Chip
                                  className={classes.chip}
                                  clickable
                                  label={item}
                                  key={id}
                                  size="small"
                                  theme={theme}
                                  chipColor={cardContrastColor}
                                  hoverable={false}
                                  multiple={tags.length > 1}
                                  onClick={(e: MouseEvent) => handleKeywordClick(e, item)}
                                  component={categoryType ? MuiButtonBase : 'span'}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    {date && (
                      <MuiHidden implementation="css" smDown>
                        <div className={classes.date}>
                          {labels?.[dateLabelField] && `${labels?.[dateLabelField]}: `}
                          <span>{date}</span>
                        </div>
                      </MuiHidden>
                    )}
                  </footer>
                </>
              )}
            </div>
            <MuiButtonBase
              {...(download ? { onClick: downloadContent } : linkProps)}
              className={clsx(classes.link, 'gtm-search-card')}
              aria-label={title}
              data-resultid={resultID}
              data-datalayer-type="link"
              data-datalayer-title={title}
            />
          </article>
          {isPartnerOnly && (
            <div className="footer-partner-only-wrap">
              <PartnerOnly blueLabel theme={theme} />
            </div>
          )}
        </AssetDownloadWrapper>
      </MuiCard>
    </div>
  );
};

export default memo(CardResult);
